import { Controller } from "@hotwired/stimulus"

// Order-an-AI-job form. Handles:
//   - Mode toggle (Enhancing upload vs Creation item list).
//   - Add / remove Creation items.
//   - Pre-fill the Creation list from a menu screenshot (server-side Gemini
//     OCR) or from a CSV upload (server-side CSV parser).
export default class extends Controller {
  static targets = [
    "mode", "enhancingSection", "creationSection",
    "itemsContainer", "itemTemplate",
    "screenshotInput", "screenshotStatus",
    "csvInput", "csvStatus"
  ]
  static values = {
    screenshotUrl: String,
    csvUrl: String
  }

  connect() {
    this.toggle()
  }

  toggle() {
    const mode = this.selectedMode()
    if (this.hasEnhancingSectionTarget) this.enhancingSectionTarget.hidden = mode !== "enhancing"
    if (this.hasCreationSectionTarget) this.creationSectionTarget.hidden = mode !== "creation"
  }

  selectedMode() {
    const checked = this.modeTargets.find((r) => r.checked)
    return checked ? checked.value : "enhancing"
  }

  addItem(event) {
    if (event) event.preventDefault()
    return this.insertItemRow()
  }

  removeItem(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-ai-job-item]")
    if (!row) return
    const destroy = row.querySelector("input[name*='[_destroy]']")
    if (destroy) {
      destroy.value = "1"
      row.style.display = "none"
    } else {
      row.remove()
    }
  }

  async analyzeMenu(event) {
    const file = event.currentTarget.files?.[0]
    if (!file) return
    if (!this.hasScreenshotUrlValue) {
      this.setStatus(this.screenshotStatusTarget, "Endpoint not configured.", "error")
      return
    }

    this.setStatus(this.screenshotStatusTarget, "Reading menu with Gemini — this can take 10–30 s…")
    const formData = new FormData()
    formData.append("screenshot", file)

    try {
      const data = await this.postFormData(this.screenshotUrlValue, formData)
      const items = Array.isArray(data.items) ? data.items : []
      if (items.length === 0) {
        this.setStatus(this.screenshotStatusTarget, "No items detected. Try a clearer screenshot.", "error")
        return
      }
      const added = this.populateItems(items)
      this.setStatus(this.screenshotStatusTarget, `Added ${added} item${added === 1 ? "" : "s"} — edit anything before submitting.`, "ok")
      event.currentTarget.value = ""
    } catch (err) {
      this.setStatus(this.screenshotStatusTarget, `Failed: ${err.message || err}`, "error")
    }
  }

  async importCsv(event) {
    const file = event.currentTarget.files?.[0]
    if (!file) return
    if (!this.hasCsvUrlValue) {
      this.setStatus(this.csvStatusTarget, "Endpoint not configured.", "error")
      return
    }

    this.setStatus(this.csvStatusTarget, "Parsing CSV…")
    const formData = new FormData()
    formData.append("csv", file)

    try {
      const data = await this.postFormData(this.csvUrlValue, formData)
      const items = Array.isArray(data.items) ? data.items : []
      if (items.length === 0) {
        this.setStatus(this.csvStatusTarget, "No rows with a title found.", "error")
        return
      }
      const added = this.populateItems(items)
      this.setStatus(this.csvStatusTarget, `Imported ${added} item${added === 1 ? "" : "s"}.`, "ok")
      event.currentTarget.value = ""
    } catch (err) {
      this.setStatus(this.csvStatusTarget, `Failed: ${err.message || err}`, "error")
    }
  }

  // --- helpers ---

  async postFormData(url, formData) {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    const resp = await fetch(url, {
      method: "POST",
      headers: { Accept: "application/json", "X-CSRF-Token": token || "" },
      body: formData,
      credentials: "same-origin"
    })
    const json = await resp.json().catch(() => ({}))
    if (!resp.ok) {
      throw new Error(json.error || `HTTP ${resp.status}`)
    }
    return json
  }

  // Drops any blank rows currently in the list, then appends one row per item.
  // Returns how many items were inserted.
  populateItems(items) {
    this.pruneEmptyRows()
    let added = 0
    items.forEach((it) => {
      const row = this.insertItemRow()
      if (!row) return
      this.setRowValue(row, "title",        it.title)
      this.setRowValue(row, "description",  it.description)
      this.setRowValue(row, "instructions", it.instructions)
      added += 1
    })
    return added
  }

  insertItemRow() {
    const html = this.itemTemplateTarget.innerHTML.replace(/__INDEX__/g, Date.now() + Math.floor(Math.random() * 1000))
    this.itemsContainerTarget.insertAdjacentHTML("beforeend", html)
    return this.itemsContainerTarget.lastElementChild
  }

  setRowValue(row, fieldName, value) {
    if (value == null) return
    const input = row.querySelector(`[name$="[${fieldName}]"]`)
    if (input) input.value = value
  }

  pruneEmptyRows() {
    this.itemsContainerTarget.querySelectorAll("[data-ai-job-item]").forEach((row) => {
      const title = row.querySelector("[name$='[title]']")?.value?.trim()
      const desc  = row.querySelector("[name$='[description]']")?.value?.trim()
      const inst  = row.querySelector("[name$='[instructions]']")?.value?.trim()
      if (!title && !desc && !inst) {
        const destroy = row.querySelector("input[name*='[_destroy]']")
        if (destroy && row.querySelector("input[name*='[id]']")) {
          destroy.value = "1"
          row.style.display = "none"
        } else {
          row.remove()
        }
      }
    })
  }

  setStatus(el, message, tone = "neutral") {
    if (!el) return
    el.textContent = message
    el.style.color =
      tone === "ok"    ? "var(--piktiz-green)" :
      tone === "error" ? "var(--piktiz-pink)"  :
                         ""
  }
}
